<template>
  <div class="game-card" @click="navigateToGame">
    <div class="card-header">
      <div class="game-icon">
        <svg v-if="game === 'poker'" viewBox="0 0 24 24" fill="currentColor">
          <!-- 扑克牌图标 -->
          <path d="M6 2C4.9 2 4 2.9 4 4v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2H6zm0 2h12v16H6V4zm2 1h2v4H8V7zm4 0h2v4h-2V7zm4 0h2v4h-2V7z"/>
        </svg>
        <svg v-else-if="game === 'sudoku'" viewBox="0 0 24 24" fill="currentColor">
          <!-- 数独网格图标 -->
          <path d="M3 3h2v2H3V3zm3 0h2v2H6V3zm3 0h2v2H9V3zm6 0h2v2h-2V3zm3 0h2v2h-2V3zM3 6h2v2H3V6zm3 0h2v2H6V6zm3 0h2v2H9V6zm6 0h2v2h-2V6zm3 0h2v2h-2V6zM3 9h2v2H3V9zm3 0h2v2H6V9zm3 0h2v2H9V9zm6 0h2v2h-2V9zm3 0h2v2h-2V9zM3 12h2v2H3v-2zm3 0h2v2H6v-2zm3 0h2v2H9v-2zm6 0h2v2h-2v-2zm3 0h2v2h-2v-2zM3 15h2v2H3v-2zm3 0h2v2H6v-2zm3 0h2v2H9v-2zm6 0h2v2h-2v-2zm3 0h2v2h-2v-2zM3 18h2v2H3v-2zm3 0h2v2H6v-2zm3 0h2v2H9v-2zm6 0h2v2h-2v-2zm3 0h2v2h-2v-2z"/>
        </svg>
        <svg v-else-if="game === 'riddles'" viewBox="0 0 24 24" fill="currentColor">
          <!-- 灯泡图标（脑筋急转弯） -->
          <path d="M12 2c-5.33 4.55-8 6.75-8 11 0 4.08 3.06 7 7 7s7-2.92 7-7c0-4.25-2.67-6.45-8-11zm0 18c-2.21 0-4-1.79-4-4h8c0 2.21-1.79 4-4 4z"/>
        </svg>
      </div>
    </div>

    <div class="card-content">
      <h3 class="game-title">{{ title }}</h3>
      <p class="game-description">{{ description }}</p>

      <div class="game-meta">
        <span class="difficulty-badge">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
          {{ difficulty }}
        </span>
      </div>
    </div>

    <div class="card-footer">
      <button class="play-button">{{ t('buttons.quickStart') }}</button>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from '@/utils/i18n'

const { t } = useI18n()

const props = defineProps({
  game: {
    type: String,
    required: true
  },
  title: {
    type: String,
    required: true
  },
  description: {
    type: String,
    required: true
  },
  difficulty: {
    type: String,
    required: true
  },
  link: {
    type: String,
    required: true
  }
})

const navigateToGame = () => {
  window.location.hash = '#' + props.link
}
</script>

<style scoped>
.game-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
  overflow: hidden;
  text-decoration: none;
  color: var(--color-text-primary);
  transition: all var(--transition-base) var(--ease-in-out);
  box-shadow: var(--shadow-sm);
}

.game-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-8px);
  border-color: var(--color-primary-orange);
}

.game-card:hover .card-header {
  background: var(--gradient-primary);
}

.game-card:hover .game-icon {
  color: white;
  transform: scale(1.1);
}

/* 卡片头部 */
.card-header {
  background: linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%);
  padding: var(--space-3xl) var(--space-base);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-base) var(--ease-in-out));
}

.game-icon {
  width: 64px;
  height: 64px;
  color: var(--color-primary-orange);
  transition: all var(--transition-base) var(--ease-in-out));
}

.game-icon svg {
  width: 100%;
  height: 100%;
}

/* 卡片内容 */
.card-content {
  flex: 1;
  padding: var(--space-xl);
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
}

.game-title {
  margin: 0;
  font-size: var(--font-size-h4);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.game-description {
  margin: 0;
  font-size: var(--font-size-small);
  color: var(--color-text-secondary);
  line-height: 1.5;
  flex: 1;
}

.game-meta {
  display: flex;
  gap: var(--space-sm);
  flex-wrap: wrap;
}

.difficulty-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
  font-size: var(--font-size-tiny);
  background-color: rgba(255, 107, 53, 0.1);
  color: var(--color-primary-orange);
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-full);
  font-weight: var(--font-weight-semibold);
}

.difficulty-badge svg {
  width: 14px;
  height: 14px;
}

/* 卡片页脚 */
.card-footer {
  padding: var(--space-base) var(--space-xl) var(--space-xl);
  border-top: 1px solid var(--color-border);
}

.play-button {
  width: 100%;
  background: var(--gradient-primary);
  color: white;
  border: none;
  padding: var(--space-md) var(--space-lg);
  border-radius: var(--radius-md);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-small);
  cursor: pointer;
  transition: all var(--transition-base) var(--ease-in-out));
  font-family: var(--font-heading);
}

.play-button:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.play-button:active {
  transform: translateY(0);
}

/* 响应式 */
@media (max-width: 640px) {
  .card-header {
    padding: var(--space-2xl) var(--space-base);
  }

  .game-icon {
    width: 48px;
    height: 48px;
  }

  .card-content {
    padding: var(--space-lg);
  }

  .game-title {
    font-size: var(--font-size-h5);
  }
}
</style>
